import { computed, ComputedRef, ref, VNode } from 'vue'
import { PanelContent, PanelContentRequest } from '@tritt/multipanel'
import { usePanelManager } from '@tritt/multipanel'

export type CommandBarOption = {
    type: string,
    typeLabel: string,
    label: string | Function,
    key: string,
    icon?: string,
    rightContent?: VNode,
    imageSrc?: string,
    showPanelSelector?: boolean,
    activate: Function,
    isActive: Function,
    isVisible?: Function,
    keywords?: string[],
    hotkey?: string
}

export type NavigationOption = {
    route: PanelContent,
    label: string,
    icon: string,
    hotkey?: string
}

const commands = ref<Map<String, CommandBarOption>>(new Map<String, CommandBarOption>())

const search = ref('')
let isInitialized = false

const initialize = () => {
    if (isInitialized) {
        return
    }
    isInitialized = true
}

const navigateToRoute = (route: PanelContent, panelId: number) => {
    const payload: PanelContentRequest = { ...route, panelId }
    const { setPanelPath } = usePanelManager()
    setPanelPath(payload)
}

const optionMatchesFilter = (option: CommandBarOption, search: string): boolean => {
    if (option.isVisible) {
        return option.isVisible(search)
    }

    const normalizedSearch = search.toLowerCase()

    const hotkeyPattern = /^([a-z])(\d?)$/
    const hotkeyMatch = normalizedSearch.match(hotkeyPattern)
    if (hotkeyMatch && option.hotkey) {
        return true
    }

    const searchTerms: string[] = [option.typeLabel.toLowerCase()]

    if (typeof option.label === 'string') {
        searchTerms.push(option.label.toLowerCase())
    }


    if (option.keywords) {
        option.keywords.forEach(keyword => {
            searchTerms.push(keyword.toLowerCase())
        })
    }

    return searchTerms.some(term => term.includes(normalizedSearch))
}

const isHotkeyMatchWithPanelTarget = (option: CommandBarOption, search: string): number | null => {
    const hotkeyPattern = /^([a-z])(\d?)$/
    const hotkeyMatch = search.toLowerCase().match(hotkeyPattern)
    if (hotkeyMatch && option.hotkey) {
        const hotkeyChar = hotkeyMatch[1]
        const panelTargetStr = hotkeyMatch[2]
        if (option.hotkey.toLowerCase() === hotkeyChar) {
            return panelTargetStr ? parseInt(panelTargetStr, 10) : 1
        }
    }
    return null
}

const filteredOptions: ComputedRef<CommandBarOption[]> = computed<CommandBarOption[]>(() => {
    const allCommands = Array.from(commands.value.values())
    const matches: CommandBarOption[] = allCommands.filter(option => {
        return optionMatchesFilter(option, search.value)
    })

    return sortMatches(matches, search.value)
})

const sortMatches = (matches: CommandBarOption[], searchValue: string): CommandBarOption[] => {
    const normalizedSearch = searchValue.toLowerCase()
    const hotkeySearch = /^[a-z]\d?$/.test(normalizedSearch)

    return [...matches].sort((a, b) => {
        if (hotkeySearch) {
            const hotkeyChar = normalizedSearch[0] // only the letter is compared

            const aHotkeyMatch = a.hotkey?.toLowerCase() === hotkeyChar
            const bHotkeyMatch = b.hotkey?.toLowerCase() === hotkeyChar

            if (aHotkeyMatch && !bHotkeyMatch) return -1
            if (!aHotkeyMatch && bHotkeyMatch) return 1
        }

        const aLabel = String(a.label).toLowerCase()
        const bLabel = String(b.label).toLowerCase()

        const aStarts = aLabel.startsWith(normalizedSearch)
        const bStarts = bLabel.startsWith(normalizedSearch)

        if (aStarts && !bStarts) return -1
        if (!aStarts && bStarts) return 1

        return 0
    })
}

const unregisterCommandsOfType = (type: string) => {
    commands.value.forEach((command, key) => {
        if (command.type === type) {
            commands.value.delete(key)
        }
    })
}

const useContaCommand = () => {
    initialize()

    const targetPanelIndex = ref(1)

    const hitCount = computed(() => {
        return filteredOptions.value.length || 0
    })

    const registerCommand = (command: CommandBarOption) => {
        commands.value.set(command.key, command)
    }

    const registerNavigationCommand = (option: NavigationOption) => {
        const command = {
            type: 'nav',
            typeLabel: 'Navigasjon',
            label: option.label,
            key: option.route.path || option.route.componentName,
            icon: option.icon,
            activate: (targetPanel: number) => navigateToRoute(option.route, targetPanel),
            isActive: () => false,
            showPanelSelector: true,
            hotkey: option.hotkey
        }
        commands.value.set(command.key, command)
    }

    return {
        search,
        filteredOptions,
        hitCount,
        targetPanelIndex,
        registerCommand,
        registerNavigationCommand,
        unregisterCommandsOfType,
        isHotkeyMatchWithPanelTarget
    }
}


export {
    useContaCommand
}